import React, { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Box, Text, Flex } from '@fundamentals'
import { FormHelperText } from '@mui/material'
import OtpInput from 'react-otp-input'
import { Button, CountdownTimer } from '@common/material'
import { useToast } from '@core/toast'
import { localizedStrings } from '@core/strings'
import {
  useCreateMfaSession,
  useSkipMFASession,
  useVerifyMFASessionCode,
} from '@core/react-query/features/auth'
import { useWebAuthContext } from 'webapp/providers/authentication'
import { isMFAWrongCodeError } from '@core/authentication/mfaHelpers'
import { CreateMFASession } from '@core/entities/auth'

interface MFAFormData {
  email: string
  mfaToken: string
  code: string
  codeValidity: number
  isCodeExpired: boolean
  resetTimer: boolean
  showSkipMFAConfirmation: boolean
  allowSkipMFA: boolean
  error: string
}

interface MFAFormProps {
  onSuccess: (token: string) => void
}

export const MFAForm: React.FC<MFAFormProps> = ({ onSuccess }) => {
  const toast = useToast()
  const {
    setMFAToken,
    getMFAToken,
    storeMFAExpiry,
    storeMFAEmail,
    getMFAEmail,
    isSessionValid,
  } = useWebAuthContext()
  const createMFASessionMutation = useCreateMfaSession()
  const verifyMFASessionCodeMutation = useVerifyMFASessionCode()
  const skipMFASessionMutation = useSkipMFASession()

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState,
  } = useForm<MFAFormData>({
    defaultValues: {
      email: '',
      mfaToken: '',
      code: '',
      codeValidity: 0,
      isCodeExpired: false,
      resetTimer: false,
      showSkipMFAConfirmation: false,
      error: '',
    },
  })

  // MARK: Manual verification
  const watchEmail = watch('email')
  const watchCode = watch('code')
  const watchCodeValidity = watch('codeValidity')
  const watchIsCodeExpired = watch('isCodeExpired')
  const watchResetTimer = watch('resetTimer')
  const watchError = watch('error')

  // MARK: Skip MFA Confirmation Flow
  const watchAllowSkipMFA = watch('allowSkipMFA')
  const watchShowSkipMFAConfirmation = watch('showSkipMFAConfirmation')

  const handleOnResendMFA = ({ force }: { force?: boolean } = {}) => {
    // Check if we should use cached session (only when force is false/undefined)
    if (!force && isSessionValid()) {
      const cachedEmail = getMFAEmail()
      if (cachedEmail) {
        setValue('email', cachedEmail)
      }

      const cachedToken = getMFAToken()
      if (cachedToken) {
        // Reuse cached session - no new code sent
        setValue('mfaToken', cachedToken)

        toast.success(localizedStrings.mfaCodeSentToEmail)

        return // Skip API call
      }
    }

    // Create new session via API
    createMFASessionMutation.mutate(
      {
        linkUrl: document.location.origin,
      },
      {
        onSuccess: (response) => {
          setValue('email', response.email)
          setValue('mfaToken', response.token)

          setMFAToken(response.token)
          storeMFAEmail(response.email)

          // Store expiry separately (calculate from tokenValidityInSeconds)
          if (response.tokenValidityInSeconds) {
            const expiresAt = new Date(
              Date.now() + response.tokenValidityInSeconds * 1000, // Convert seconds to milliseconds
            ).toISOString()
            storeMFAExpiry(expiresAt)
          }

          toast.success(localizedStrings.mfaCodeSentToEmail)

          // Set the code validity for the timer component (in seconds)
          if (response.codeValidity) {
            setValue('codeValidity', response.codeValidity)
            setValue('isCodeExpired', false)
            setValue('resetTimer', !watchResetTimer) // Toggle to force timer reset
          }

          setValue('allowSkipMFA', response.allowSkip)
        },
        onError: () => {
          toast.error(localizedStrings.mfaErrorSendingCode)
        },
      },
    )
  }

  const handleCodeExpire = () => {
    setValue('isCodeExpired', true)
  }

  const handleOnShowSkipMFAConfirmation = () => {
    setValue('showSkipMFAConfirmation', true)
  }

  const handleOnValidateFromEmail = () => {
    setValue('showSkipMFAConfirmation', false)
  }

  const handleOnSkipMFA = () => {
    skipMFASessionMutation.mutate(
      {},
      {
        onSuccess: () => {
          toast.success(localizedStrings.mfaSkippedSuccessfully)
          onSuccess(createMFASessionMutation.data?.token)
        },
        onError: (error) => {
          toast.error(error?.message)
        },
      },
    )
  }

  const handleOnLogout = () => {
    API.logout()
  }

  // Auto-request MFA code on component mount
  useEffect(() => {
    handleOnResendMFA()
  }, [])

  // Auto-submit when 6 digits are entered manually
  useEffect(() => {
    if (watchCode.length === 6) {
      const remoteCallHandleSubmit = handleSubmit((data) =>
        onSubmit({ ...data, code: watchCode }),
      )

      remoteCallHandleSubmit()
    }
  }, [watchCode])

  const onSubmit: SubmitHandler<MFAFormData> = (data: MFAFormData) => {
    // Clear any existing form errors when submitting
    setValue('error', '')
    clearErrors()

    verifyMFASessionCodeMutation.mutate(
      {
        token: data.mfaToken,
        code: data.code,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.mfaCodeVerified)
          onSuccess(data.mfaToken)
        },
        onError: (error) => {
          const errorData = error?.axiosError?.response?.data as any
          const errorValue = errorData?.errorType ?? error?.message
          setValue('error', errorValue)
        },
      },
    )
  }

  const handleOnPaste: React.ClipboardEventHandler = (event) => {
    const pastedCode = event.clipboardData.getData('text')?.trim()
    const isOnlyDigits = /^\d+$/.test(pastedCode)
    const hasMoreThan6Digits = pastedCode?.length > 6

    if (!isOnlyDigits) {
      event.preventDefault()
      setValue('code', '')
      setError('code', { message: localizedStrings.mfaCodeMustBeOnlyNumbers })
      toast.error(localizedStrings.mfaCodeMustBeOnlyNumbers)
      return
    }

    if (hasMoreThan6Digits) {
      event.preventDefault()
      setValue('code', '')
      setError('code', { message: localizedStrings.mfaCodeMustBe6Digits })
      toast.error(localizedStrings.mfaCodeMustBe6Digits)
      return
    }

    setValue('code', pastedCode)
  }

  if (watchShowSkipMFAConfirmation) {
    return (
      <Box>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          textAlign='left'
          mb={4}
          p={3}
        >
          <Text
            mb={2}
            fontSize={16}
            fontWeight={500}
            color='text.primary'
            lineHeight={1.5}
          >
            {localizedStrings.mfaSkipUntil(
              CreateMFASession.getEnforceOnDate(
                createMFASessionMutation.data?.enforceOn,
              ),
            )}
          </Text>
          <Text
            mb={2}
            fontSize={15}
            fontWeight={400}
            color='text.secondary'
            lineHeight={1.6}
          >
            {localizedStrings.mfaSkipUntilDescription(
              CreateMFASession.getSkipDurationTime(
                createMFASessionMutation.data?.skipDurationSeconds,
              ),
            )}
          </Text>
          <Text
            fontSize={15}
            fontWeight={400}
            color='text.secondary'
            lineHeight={1.6}
          >
            {localizedStrings.mfaSkipUntilDescription2(
              CreateMFASession.getTokenValidityTime(
                createMFASessionMutation.data?.tokenValidityInSeconds,
              ),
            )}
          </Text>
        </Box>

        <Box display='flex' flexDirection='row' gap={2}>
          <Button
            variant='outlined'
            color='primary'
            fullWidth
            onClick={handleOnValidateFromEmail}
            sx={{
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {localizedStrings.mfaValidateFromEmail}
          </Button>

          <Button
            data-test='mfa-skip-button'
            variant='outlined'
            color='error'
            fullWidth
            onClick={handleOnSkipMFA}
            sx={{
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {localizedStrings.skip}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        mb={4}
      >
        <Text variant='h4' mb={2}>
          {localizedStrings.mfaVerification}
        </Text>
        <Text
          fontSize={16}
          fontWeight={400}
          lineHeight='24px'
          textAlign='center'
          color='text.secondary'
        >
          {localizedStrings.mfaEnterCodeDescription(watchEmail)}
        </Text>
      </Box>

      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Box mb={2}>
          <Text fontSize={14} color='text.secondary' mb={1}>
            {localizedStrings.code}
          </Text>
          <Controller
            name='code'
            control={control}
            rules={{
              required: localizedStrings.required,
              pattern: {
                value: /^\d{6}$/,
                message: localizedStrings.mfaCodeMustBe6Numbers,
              },
              minLength: {
                value: 6,
                message: localizedStrings.mfaCodeMustBe6Digits,
              },
              maxLength: {
                value: 6,
                message: localizedStrings.mfaCodeMustBe6Digits,
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <Flex flexDirection='column'>
                  <OtpInput
                    {...field}
                    inputType='tel' // Use tel instead of number to remove the default style for number input where we have arrows for increasing and decreasing the value
                    shouldAutoFocus
                    numInputs={6}
                    renderSeparator={<span>-</span>}
                    renderInput={({ style, ...props }, index) => (
                      <input
                        {...props}
                        data-test={index === 0 ? 'mfa-code-input' : undefined}
                        inputMode='numeric' // Needed to remove the default style for number input where we have arrows for increasing and decreasing the value
                        pattern='[0-9]*' // Needed to remove the default style for number input where we have arrows for increasing and decreasing the value
                        style={{
                          ...style,
                          marginLeft: index === 0 ? '0px' : '9px',
                          marginRight: index === 5 ? '0px' : '9px',
                        }}
                      />
                    )}
                    inputStyle={{
                      width: '48px',
                      height: '48px',
                      textAlign: 'center',
                      marginLeft: '9px',
                      marginRight: '9px',
                      fontSize: '24px',
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.3)',
                    }}
                    onChange={(value) => {
                      if (watchError) {
                        setValue('error', '')
                      }
                      field.onChange(value)
                    }}
                    onPaste={handleOnPaste}
                  />
                  {fieldState.error && (
                    <FormHelperText error sx={{ marginY: 1 }}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </Flex>
              )
            }}
          />
        </Box>

        {watchError && isMFAWrongCodeError(watchError) && (
          <Box mb={2}>
            <Text
              data-test='mfa-wrong-verification-code-text'
              color='error.main'
              fontSize={14}
            >
              {localizedStrings.mfaWrongVerificationCode}
            </Text>
          </Box>
        )}

        <CountdownTimer
          durationSeconds={watchCodeValidity}
          onExpire={handleCodeExpire}
          reset={watchResetTimer}
          mt={1}
          mb={2}
          display='flex'
          justifyContent='center'
        />

        <Box mb={2}>
          <Button
            data-test='mfa-resend-code-button'
            variant='outlined'
            color='primary'
            fullWidth
            onClick={() => handleOnResendMFA({ force: true })}
            disabled={createMFASessionMutation.isLoading}
          >
            {localizedStrings.mfaSendAnotherCode}
          </Button>
        </Box>

        <Button
          data-test='mfa-verify-code-button'
          type='submit'
          variant='contained'
          color='primary'
          fullWidth
          disabled={
            verifyMFASessionCodeMutation.isLoading ||
            watchIsCodeExpired ||
            !formState.isValid
          }
        >
          {verifyMFASessionCodeMutation.isLoading
            ? localizedStrings.mfaVerifying
            : localizedStrings.mfaVerifyCode}
        </Button>
      </Box>

      {watchAllowSkipMFA && (
        <Box
          mt={4}
          p={2}
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Text
            fontSize={16}
            fontWeight={500}
            color='text.primary'
            mb={1}
            textAlign='center'
          >
            {localizedStrings.mfaCanNotAccessEmail}
          </Text>

          <Text fontSize={14} color='text.secondary' mb={2} textAlign='center'>
            {localizedStrings.mfaNeedToSkipMfaPrefix}
            <Text
              component='span'
              fontSize={14}
              fontWeight={500}
              color='text.primary'
            >
              {CreateMFASession.getEnforceOnDate(
                createMFASessionMutation.data?.enforceOn,
              )}
            </Text>
            {'.'}
            {localizedStrings.mfaNeedToSkipMfaSuffix}
          </Text>

          <Button
            data-test='mfa-skip-flow-button'
            variant='outlined'
            color='warning'
            fullWidth
            onClick={handleOnShowSkipMFAConfirmation}
          >
            {localizedStrings.mfaSkipForNow}
          </Button>
        </Box>
      )}

      <Box
        mt={4}
        p={2}
        sx={{
          backgroundColor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Text fontSize={14} color='text.secondary' mb={2} textAlign='center'>
          {localizedStrings.mfaNeedToSignInWithDifferentAccount}
        </Text>
        <Button
          data-test='mfa-logout-button'
          variant='outlined'
          color='error'
          fullWidth
          onClick={handleOnLogout}
          sx={{
            py: 1,
            fontWeight: 500,
            textTransform: 'none',
          }}
        >
          {localizedStrings.logout}
        </Button>
      </Box>
    </Box>
  )
}
